import * as React from "react"
import { ReportKind } from "@digits-graphql/frontend/graphql-bearer"
import type { DigitsRoute } from "@digits-shared/components/Router/DigitsRoute"
import { LoggedRedirect } from "@digits-shared/components/Router/LoggedRedirect"
import { useCurrentRoute } from "@digits-shared/hooks/useCurrentRoute"
import useSession from "@digits-shared/hooks/useSession"
import { useQuarterlyDashboardPathGenerator } from "src/frontend/hooks/useQuarterlyDashboardPathGenerator"
import routes from "src/frontend/routes"
import type FrontendSession from "src/frontend/session"

export const MarketingRedirect: React.FC = () => {
  const currentRoute = useCurrentRoute(routes)
  const { currentLegalEntity } = useSession<FrontendSession>()
  const generatePath = useQuarterlyDashboardPathGenerator()

  const generateToLE = (route: DigitsRoute) =>
    generatePath(route, { leSlug: currentLegalEntity.slug })

  const generateToReports = () =>
    generatePath(routes.financials, {
      leSlug: currentLegalEntity.slug,
      kind: ReportKind.ProfitAndLoss,
    })

  if (!currentLegalEntity?.slug) {
    return <LoggedRedirect name="marketingRedirect-noLE" to={generatePath(routes.root)} />
  }

  if (currentLegalEntity?.isNew || currentLegalEntity?.isPending) {
    return <LoggedRedirect name="marketingRedirect-newOrPendingLE" to={generatePath(routes.root)} />
  }

  switch (currentRoute) {
    case routes.redirectInbox:
      return <LoggedRedirect name="marketingRedirect-inbox" to={generateToLE(routes.inbox)} />
    case routes.redirectAnalysis:
      return <LoggedRedirect name="marketingRedirect-analysis" to={generateToLE(routes.analysis)} />
    case routes.redirectReports:
      return <LoggedRedirect name="marketingRedirect-reports" to={generateToReports()} />
    case routes.redirectFinancials:
      return <LoggedRedirect name="marketingRedirect-financials" to={generateToReports()} />
    case routes.redirectBillPay:
      return <LoggedRedirect name="marketingRedirect-billPay" to={generateToLE(routes.billPay)} />
    case routes.redirectInvoicing:
      return (
        <LoggedRedirect name="marketingRedirect-invoicing" to={generateToLE(routes.invoicing)} />
      )
    case routes.redirectTransactions:
      return (
        <LoggedRedirect
          name="marketingRedirect-transactions"
          to={generateToLE(routes.accounting)}
        />
      )
    default:
      return <LoggedRedirect name="marketingRedirect-notFound" to={generatePath(routes.root)} />
  }
}
